import type { BaseLinkRoute, PrevNextResult } from '@connected/core';
import type { LinkProps } from '@tanstack/react-router';

import { resolvePath } from '@connected/core';
import { useParams, useSearch } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Section } from '../../layouts';

// ============================================================================
// Types
// ============================================================================

export type FooterNavigationProps<TContext> = {
    /**
     * Hook that returns prev/next navigation for the current route.
     */
    usePrevNextNavigation: () => PrevNextResult<TContext>;
    /**
     * Function to render the label for a route.
     * Typically used for i18n translation.
     */
    renderLabel: (route: BaseLinkRoute<TContext>) => string;
    /**
     * Children to render after the prev link and before the next link.
     */
    children?: React.ReactNode;
};

// ============================================================================
// Component
// ============================================================================

/**
 * Generic footer navigation component that renders Section.Footer with prev/next links.
 * Works with any app's navigation system.
 *
 * @example
 * // In your app:
 * import { FooterNavigation } from '@connected/ui/features/navigation';
 * import { usePrevNextNavigation } from './hooks/use-navigation';
 *
 * function MyFooterNavigation() {
 *     const { t } = useTranslation('navigation');
 *     return (
 *         <FooterNavigation
 *             usePrevNextNavigation={usePrevNextNavigation}
 *             renderLabel={(route) => t(route.name)}
 *         />
 *     );
 * }
 */
export function FooterNavigation<TContext>({
    usePrevNextNavigation,
    renderLabel,
    children,
}: FooterNavigationProps<TContext>) {
    const { prev, next } = usePrevNextNavigation();
    const params = useParams({ strict: false });
    const search = useSearch({ strict: false });

    // If no prev and no next, don't render anything
    if (!prev && !next) {
        return null;
    }

    return (
        <>
            {prev && (
                <Section.Footer.Link
                    to={resolvePath(prev.path, params) as LinkProps['to']}
                    search={search}
                    params={params}
                >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    {renderLabel(prev)}
                </Section.Footer.Link>
            )}
            {children}
            {next && (
                <Section.Footer.Link
                    to={resolvePath(next.exitTo ?? next.path, params) as LinkProps['to']}
                    search={search}
                    params={params}
                >
                    {renderLabel(next)}
                    <ChevronRight className="h-4 w-4 ml-1" />
                </Section.Footer.Link>
            )}
        </>
    );
}
